/**
 * React Query mutation hooks for benefit classes data modifications
 *
 * These hooks handle updates and deletes with automatic cache invalidation.
 */

import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import {
  batchUpdateBenefitClasses,
  deleteBenefitClass,
  createBenefitClass,
} from "./client";
import {
  BatchUpdateBenefitClassesRequest,
  BenefitClassResponse,
  CreateBenefitClassRequest,
} from "./client";
import { queryKeys } from "./queries";

const invalidatePlanDesignerQueries = async (
  queryClient: QueryClient,
  rfpId: string,
): Promise<void> => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.benefitClasses(rfpId),
  });
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        (key[0] === "rfp" && key[1] === rfpId) ||
        (key[0] === "benefitPlanGroups" && key[1] === rfpId) ||
        (key[0] === "coverage" && key[2] === rfpId)
      );
    },
  });
};

/**
 * Hook to create a new benefit class
 *
 * Automatically invalidates related queries on success.
 *
 * @param rfpId - RFP ID for cache invalidation
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function CreateClassButton({ rfpId }: { rfpId: string }) {
 *   const mutation = useCreateBenefitClass(rfpId, {
 *     onSuccess: () => {
 *       console.log("Class created!");
 *     }
 *   });
 *
 *   const handleCreate = () => {
 *     mutation.mutate({
 *       rfpId,
 *       benefitClass: { name: "Class F", coverageIds: [] }
 *     });
 *   };
 *
 *   return (
 *     <button onClick={handleCreate} disabled={mutation.isPending}>
 *       {mutation.isPending ? "Creating..." : "Create Class"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useCreateBenefitClass = (
  rfpId: string,
  options?: UseMutationOptions<
    BenefitClassResponse,
    Error,
    CreateBenefitClassRequest
  >,
): UseMutationResult<
  BenefitClassResponse,
  Error,
  CreateBenefitClassRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();

  return useMutation({
    mutationFn: (request: CreateBenefitClassRequest) =>
      createBenefitClass(request, axiosInstance),
    onSuccess: (_data, _variables, _context) => {
      invalidatePlanDesignerQueries(queryClient, rfpId);
    },
    ...options,
  });
};

/**
 * Hook to batch update benefit classes
 *
 * Automatically invalidates related queries on success.
 *
 * @param rfpId - RFP ID for cache invalidation
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function BenefitClassEditor({ rfpId }: { rfpId: string }) {
 *   const mutation = useBatchUpdateBenefitClasses(rfpId, {
 *     onSuccess: () => {
 *       toast.success("Benefit classes updated!");
 *     },
 *     onError: (error) => {
 *       toast.error(`Failed: ${error.message}`);
 *     }
 *   });
 *
 *   const handleSave = () => {
 *     mutation.mutate({
 *       updates: [
 *         { id: "class-1", name: "Class A", coverageIds: ["cov-1", "cov-2"] }
 *       ]
 *     });
 *   };
 *
 *   return (
 *     <button onClick={handleSave} disabled={mutation.isPending}>
 *       {mutation.isPending ? "Saving..." : "Save"}
 *     </button>
 *   );
 * }
 * ```
 */
export const useBatchUpdateBenefitClasses = (
  rfpId: string,
  options?: UseMutationOptions<
    BenefitClassResponse[],
    Error,
    BatchUpdateBenefitClassesRequest
  >,
): UseMutationResult<
  BenefitClassResponse[],
  Error,
  BatchUpdateBenefitClassesRequest
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();

  return useMutation({
    mutationFn: (request: BatchUpdateBenefitClassesRequest) =>
      batchUpdateBenefitClasses(request, axiosInstance),
    onSuccess: (_data, _variables, _context) =>
      invalidatePlanDesignerQueries(queryClient, rfpId),
    ...options,
  });
};

/**
 * Hook to delete a benefit class
 *
 * Automatically invalidates related queries on success.
 *
 * @param rfpId - RFP ID for cache invalidation
 * @param options - React Query mutation options
 * @returns Mutation result
 *
 * @example
 * ```tsx
 * function DeleteButton({ rfpId, classId }: { rfpId: string; classId: string }) {
 *   const mutation = useDeleteBenefitClass(rfpId, {
 *     onSuccess: () => {
 *       toast.success("Benefit class deleted!");
 *     }
 *   });
 *
 *   return (
 *     <button
 *       onClick={() => mutation.mutate(classId)}
 *       disabled={mutation.isPending}
 *     >
 *       Delete
 *     </button>
 *   );
 * }
 * ```
 */
export const useDeleteBenefitClass = (
  rfpId: string,
  options?: UseMutationOptions<void, Error, string>,
): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApi();

  return useMutation({
    mutationFn: (classId: string) => deleteBenefitClass(classId, axiosInstance),
    onSuccess: (_data, _variables, _context) => {
      invalidatePlanDesignerQueries(queryClient, rfpId);
    },
    ...options,
  });
};
